import React, { useMemo } from "react";
import { Navigate, Outlet } from "react-router-dom";
import waytora_logo from "@/assets/logos/internal/waytora_white_sm_logo.svg";
import useAuthStore from "@/stores/user/authStore";

const LoadingState = () => {
  return (
    <div className="min-h-screen w-full flex justify-center items-center">
      <div className="scale-animation h-24 w-24 bg-indigo-700 rounded-full flex justify-center items-center transition-all duration-300 hover:bg-indigo-800 ring-2 shadow-lg shadow-indigo-200 ring-offset-4 ring-indigo-400">
        <img
          src={waytora_logo}
          className="w-20 h-20"
          alt="Waytora Logo"
          draggable="false"
        />
      </div>
    </div>
  );
};

interface RouteConfig {
  hasAccess: boolean;
  isBeta: boolean;
  isComingSoon: boolean;
}

const ProtectedRoute: React.FC = () => {
  const isLoading = useAuthStore((state) => state.initialLoading);

  // Memoize static values
  const routeConfig = useMemo<RouteConfig>(
    () => ({
      hasAccess: sessionStorage.getItem("app-access") === "true",
      isBeta: import.meta.env.VITE_BETA_ENABLED === "true",
      isComingSoon: import.meta.env.VITE_COMING_SOON === "true",
    }),
    []
  );

  if (isLoading) {
    return <LoadingState />;
  }

  // If coming soon is enabled, only allow access to public routes
  if (routeConfig.isComingSoon) {
    const publicRoutes = ["/", "/about", "/contact", "/contact/us"];
    const currentPath = window.location.pathname;

    if (publicRoutes.includes(currentPath)) {
      return <Outlet />;
    }
    return <Navigate to="/" replace />;
  }

  // If beta is not enabled or user has access, show content
  if (!routeConfig.isBeta || routeConfig.hasAccess) {
    return <Outlet />;
  }

  // Otherwise, redirect to protect page
  return <Navigate to="/protect" replace />;
};

export default ProtectedRoute;
